import { useTranslation } from "react-i18next";
import { Grid, IconButton } from "@mui/material";
import {
  AddBoxOutlined,
  ApprovalOutlined,
  BusinessOutlined,
  CalendarMonthOutlined,
  DeleteOutline,
  DescriptionOutlined,
  IndeterminateCheckBoxOutlined,
  InfoOutlined,
  LocationOnOutlined,
  PeopleOutline,
  PersonOutline,
} from "@mui/icons-material";
import _ from "lodash";
import { DATE_FORMAT, DateFieldMap, DateTypeOptionIds, ELEMENT_ID } from "utils/constants";
import {
  deleteFromBasket,
  ModificationStatus,
  ProductBasket,
  ProductDetails,
  removeFromBasket,
  updateBasket,
} from "store/bookingJourney";
import { useStyles } from "./griddetails-style";
import {
  checkIsVirtual,
  isIBFPublicBooking,
  dispatchError,
  dispatchSuccess,
  NumberFormatter,
  populateEventFromProduct,
  showCostCentre,
  formatPreferredDate,
  enableAlternativeDate,
  enableApprovalId,
  enableBusinessUnit,
  getFormattedCostCentre,
} from "utils/helpers";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "store";
import { ConfirmationPopup, EditLink } from "components";
import { BookingJourneyStep, CreationMethod, DateFieldConfig } from "utils/types";
import { useEffect, useState } from "react";
import { Booking, Event } from "utils/graphql/bookingJourney/models";
import { useMutation } from "@apollo/client/react";
import { ADD_UPDATE_DELETE_EVENTS } from "utils/graphql/bookingJourney/booking-journey-queries";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

type GridDetailsProps = {
  uniqueId: string;
  product: ProductBasket;
  productDetails: ProductDetails;
  showDateAndLocation: boolean;
  showHostDelegates: boolean;
  showAdditionalInfo: boolean;
  showEditLink: boolean;
  showQtyLinks?: boolean;
  showAvailabilityValidation?: boolean;
  showAlternativeDates?: boolean;
};
export const GridDetails: React.FC<GridDetailsProps> = ({
  uniqueId,
  product,
  productDetails,
  showDateAndLocation,
  showHostDelegates,
  showAdditionalInfo,
  showEditLink,
  showQtyLinks,
  showAvailabilityValidation,
  showAlternativeDates,
}) => {
  const { t } = useTranslation();
  const userContext = useSelector((state: RootState) => state.core.userContext);
  const basket = useSelector((state: RootState) => state.bookingJourney.basket);
  const classes = useStyles();
  const showDelegateDetails = (hasDelegate: boolean, bookingType: string) => {
    return hasDelegate || isIBFPublicBooking(bookingType);
  };

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState<string>("");
  const isUnavailable = showAvailabilityValidation && !product.isValid;
  const dateTypeMap = DateFieldMap[productDetails?.dateType];

  const handleAction = (action: any) => {
    if (action.type === removeFromBasket.type) {
      if (action.payload?.quantity === 1) {
        setShowConfirmDialog(true);
        return;
      }
    }
    dispatch(action);
    setStatusMessage(action.type + " success");
  };

  const getDateString = () => {
    const fieldsConfig: DateFieldConfig[] = dateTypeMap?.dateTypeFieldConfig;
    if (!fieldsConfig || fieldsConfig.length === 0) return "";

    if (productDetails?.dateType === DateTypeOptionIds.DateRange) {
      const [start, end] = fieldsConfig;
      return `${t("daterange-from")} ${format(
        productDetails[start.field],
        DATE_FORMAT.ddddDMMMMyyyy,
      )} ${t("daterange-to")} ${format(productDetails[end.field], DATE_FORMAT.ddddDMMMMyyyy)}`;
    }
    return format(
      productDetails[fieldsConfig[0].field],
      fieldsConfig[0].isMonth ? DATE_FORMAT.MMMMYYYY : DATE_FORMAT.ddddDMMMMyyyy,
    );
  };

  const handleDeleteProduct = () => {
    if (product) {
      const eventArray: Partial<Event>[] = product.productDetails
        .map((detail): Partial<Event> => {
          // Check if productDetails is passed
          if (productDetails) {
            // If productDetails is passed and matches the current detail, delete the specific event
            if (detail.id === productDetails.id) {
              return populateEventFromProduct(product, {
                ...detail,
                modificationStatus: ModificationStatus.Deleted,
              });
            }
          } else {
            // If productDetails is not passed (called from the summary page), mark the entire product as deleted
            return populateEventFromProduct(
              {
                ...product,
                modificationStatus: ModificationStatus.Deleted,
              },
              detail,
            );
          }
        })
        .filter((p) => p);

      const eventRequest: Booking = {
        bookingId: basket.bookingId,
        events: eventArray,
        creationMethod: CreationMethod.Ibf,
      };
      if (eventArray?.length)
        deleteEvents({
          variables: {
            body: eventRequest,
          },
        });
    }
  };
  const [deleteEvents, { loading }] = useMutation(ADD_UPDATE_DELETE_EVENTS, {
    onCompleted: (data: any) => {
      dispatch({ type: "loader/showandhide", payload: { show: false } });
      if (!data?.addEventsFromBookingJourney) {
        dispatchError(dispatch, t);
        return;
      }
      handleAction(
        deleteFromBasket({ productId: product.id, productDetailId: productDetails?.id }),
      );
      dispatchSuccess(dispatch, t);
      if (!data.addEventsFromBookingJourney.events?.length) {
        //No events returned when all products are deleted,redirect to home page
        navigate("/");
      }
    },
    onError: () => {
      dispatch({ type: "loader/showandhide", payload: { show: false } });
      dispatchError(dispatch, t);
    },
  });

  useEffect(() => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: loading, message: loading ? t("loading") : "" },
    });
  }, [loading, dispatch, t]);

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <div className="sr-only" role="status" aria-atomic="true" aria-live="assertive">
          {statusMessage}
        </div>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }} className={classes.courseIdContainer}>
        <h3 id={`${uniqueId}-${ELEMENT_ID.COURSEID}-heading`}>
          {t("course_id_upper")}: {product.customerProductCode}
        </h3>
        <hr aria-hidden="true" className={classes.horizontalLine} />
      </Grid>
      <Grid size={{ xs: 12, sm: showQtyLinks ? 6 : 8, md: showQtyLinks ? 6 : 8 }}>
        <Grid container direction="column">
          <Grid
            className={isUnavailable ? classes.unavailableTitle : classes.title}
            id={`${uniqueId}-${ELEMENT_ID.NAME}`}
          >
            {product.title}
          </Grid>
          <Grid
            className={isUnavailable ? classes.unavailableDescription : classes.description}
            id={`${uniqueId}-${ELEMENT_ID.DESCRIPTION}`}
          >
            {`${product.deliveryMethod}, ${product.bookingType}${
              !checkIsVirtual(product.deliveryMethod) && product.venue ? `, ${product.venue}.` : "."
            }`}
          </Grid>
          {isUnavailable && (
            <Grid
              id={`${uniqueId}-${ELEMENT_ID.COURSE_NOT_AVAILABLE}`}
              className={classes.unavailableMessage}
              role="alert"
            >
              {t("product_unavailable_text")}
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, sm: 2, md: 2 }}>
        <Grid container direction="column" className={classes.removeColumn}>
          <Grid className={classes.columnHeader}>{t("remove")}</Grid>
          <Grid className={classes.actionContent}>
            <IconButton
              className={classes.iconBtn}
              id={`${uniqueId}-${ELEMENT_ID.DELETE}`}
              aria-label={t("remove")}
              component="span"
              onClick={() => setShowConfirmDialog(true)}
              aria-describedby={
                isUnavailable ? `${uniqueId}-${ELEMENT_ID.COURSE_NOT_AVAILABLE}` : undefined
              }
              size="large"
            >
              <DeleteOutline className={classes.iconClass} />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      {showQtyLinks && (
        <Grid size={{ xs: 12, sm: 2, md: 2 }}>
          <Grid container direction="column">
            <Grid className={classes.columnHeader}>{t("qty")}</Grid>
            <Grid className={classes.actionContent}>
              <div className={classes.qtyControls}>
                <IconButton
                  id={`${uniqueId}-${ELEMENT_ID.REMOVE}`}
                  aria-label={t("decrease_quantity", {
                    courseId: product.courseId,
                  })}
                  disabled={isUnavailable}
                  component="span"
                  onClick={() => handleAction(removeFromBasket(product))}
                  size="large"
                >
                  <IndeterminateCheckBoxOutlined className={classes.iconClass} />
                </IconButton>
                <span className={classes.quantity}>{product.quantity}</span>
                <IconButton
                  id={`${uniqueId}-${ELEMENT_ID.ADD}`}
                  aria-label={t("increase_quantity", {
                    courseId: product.courseId,
                  })}
                  disabled={isUnavailable}
                  component="span"
                  onClick={() => handleAction(updateBasket(product))}
                  size="large"
                >
                  <AddBoxOutlined className={classes.iconClass} />
                </IconButton>
              </div>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 2, md: 2 }} className={classes.actionColumn}>
        <dl className={classes.actionDl}>
          <dt className={classes.columnHeader}>{t("price")}</dt>
          <dd className={classes.priceContent}>
            {NumberFormatter(product.currency).format(product.price)}
          </dd>
        </dl>
      </Grid>
      <ul className={classes.infoList}>
        {showDateAndLocation &&
          (enableAlternativeDate() ? productDetails.preferredDate : dateTypeMap) && (
            <li>
              <Grid
                size={{ xs: 12 }}
                className={`${classes.filledInfoFirst} ${classes.overflowText}`}
              >
                <CalendarMonthOutlined className={classes.icon} />
                {enableAlternativeDate()
                  ? `${t("preferred-date")}: ${formatPreferredDate(productDetails.preferredDate)}`
                  : `${t(dateTypeMap.bookingJourneyGridLabel)}: ${getDateString()}`}
                {showEditLink && (
                  <EditLink
                    goto={BookingJourneyStep.DateAndLocation}
                    ariaLabel={`${t("edit")} ${t("dateandlocation")}`}
                  />
                )}
              </Grid>
            </li>
          )}
        {showAlternativeDates && productDetails.alternativeDates && (
          <li>
            <Grid size={{ xs: 12 }} className={`${classes.filledInfo} ${classes.overflowText}`}>
              <Grid container alignItems="flex-start">
                <CalendarMonthOutlined className={classes.icon} />
                <Grid className={classes.delegateLabel} style={{ flexShrink: 0 }}>
                  {t("alternative-date-label") + ":"}
                </Grid>
                <Grid className={classes.delegateList}>
                  <ol>
                    {productDetails.alternativeDates
                      .split(",")
                      .map((date) => formatPreferredDate(date.trim()))
                      .filter((date) => date)
                      .map((formattedDate, index) => (
                        <li className={classes.overflowText} key={index}>
                          {formattedDate}
                        </li>
                      ))}
                  </ol>
                </Grid>
                <Grid>
                  {showEditLink && (
                    <EditLink
                      goto={BookingJourneyStep.DateAndLocation}
                      ariaLabel={`${t("edit")} ${t("dateandlocation")}`}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </li>
        )}
        {showDateAndLocation && productDetails.location && (
          <li>
            <Grid size={{ xs: 12 }} className={`${classes.filledInfo} ${classes.overflowText}`}>
              <LocationOnOutlined className={classes.icon} />
              {t("booking_journey_location")}: {productDetails.location}
              {showEditLink && (
                <EditLink
                  goto={BookingJourneyStep.DateAndLocation}
                  ariaLabel={`${t("edit")} ${t("dateandlocation")}`}
                />
              )}
            </Grid>
          </li>
        )}
        {showHostDelegates && (
          <>
            {!isIBFPublicBooking(product.bookingType) && (
              <li>
                <Grid size={{ xs: 12 }} className={`${classes.filledInfo} ${classes.overflowText}`}>
                  <PersonOutline className={classes.icon} />
                  {productDetails.hostAndDelegates.areYouHost
                    ? `${t("host")}: ${userContext.userContext.firstName} ${
                        userContext.userContext.lastName
                      }, e: ${userContext.userContext.learnerEmail}${
                        userContext.userContext.mobileNumber
                          ? `, t: ${userContext.userContext.mobileNumber}`
                          : ""
                      }`
                    : `${t("host")}: ${productDetails.hostAndDelegates.hostFirstName} ${
                        productDetails.hostAndDelegates.hostLastName
                      }, e: ${productDetails.hostAndDelegates.hostEmailAddress}, t: ${
                        productDetails.hostAndDelegates.hostPhoneNumber
                      }`}

                  {showEditLink && (
                    <EditLink
                      goto={BookingJourneyStep.HostDelegatesPage}
                      ariaLabel={`${t("edit")} ${t("hostdelegatespage")}`}
                    />
                  )}
                </Grid>
              </li>
            )}
            {showDelegateDetails(
              productDetails.hostAndDelegates.hasDelegates,
              product.bookingType,
            ) && (
              <li>
                <Grid size={{ xs: 12 }} className={`${classes.filledInfo} ${classes.overflowText}`}>
                  <Grid container>
                    <PeopleOutline className={classes.icon} />

                    <Grid size="auto" className={classes.delegateLabel}>
                      {t("delegates") + ":"}
                    </Grid>
                    <Grid className={classes.delegateList}>
                      <ol>
                        {productDetails.hostAndDelegates.delegateDetails.map(
                          (delegateDetail, index) => (
                            <li
                              className={classes.overflowText}
                              id={`${ELEMENT_ID.PAYMENT}-${ELEMENT_ID.DELEGATE}-${index + 1}`}
                              key={index}
                            >
                              {`${delegateDetail.firstName} ${delegateDetail.lastName}`}
                            </li>
                          ),
                        )}
                      </ol>
                    </Grid>
                    <Grid>
                      {showEditLink && (
                        <EditLink
                          goto={BookingJourneyStep.HostDelegatesPage}
                          ariaLabel={`${t("edit")} ${t("hostdelegatespage")}`}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </li>
            )}
          </>
        )}
        {showAdditionalInfo && (
          <>
            <li>
              <Grid size={{ xs: 12 }} className={classes.filledInfo}>
                <InfoOutlined className={classes.additionalInfoIcon} />
                <span className={`${classes.additionalNote} ${classes.overflowText}`}>
                  {t("event_notes")}: {productDetails.eventNotes || t("not_provided")}
                </span>
                {showEditLink && (
                  <EditLink
                    goto={BookingJourneyStep.Payment}
                    ariaLabel={`${t("edit")} ${t("payment")}`}
                  />
                )}
              </Grid>
            </li>
            {!showCostCentre() && (
              <li>
                <Grid size={{ xs: 12 }} className={classes.filledInfo}>
                  <DescriptionOutlined className={classes.icon} />
                  <span className={`${classes.overflowText}`}>
                    {t("po_number")}: {productDetails.poNumber || t("not_provided")}
                  </span>
                  {showEditLink && (
                    <EditLink
                      goto={BookingJourneyStep.Payment}
                      ariaLabel={`${t("edit")} ${t("payment")}`}
                    />
                  )}
                </Grid>
              </li>
            )}
            {showCostCentre() && (
              <li>
                <Grid size={{ xs: 12 }} className={classes.filledInfo}>
                  <DescriptionOutlined className={classes.icon} />
                  <span className={classes.overflowText}>
                    {t("cost_centre")}:{" "}
                    {getFormattedCostCentre(productDetails) || t("not_provided")}
                  </span>
                  {showEditLink && (
                    <EditLink
                      goto={BookingJourneyStep.Payment}
                      ariaLabel={`${t("edit")} ${t("payment")}`}
                    />
                  )}
                </Grid>
              </li>
            )}
            {enableApprovalId() && (
              <li>
                <Grid size={{ xs: 12 }} className={classes.filledInfo}>
                  <ApprovalOutlined className={classes.icon} />
                  <span className={classes.overflowText}>
                    {t("payment_approval_id")}: {productDetails.approvalId || t("not_provided")}
                  </span>
                  {showEditLink && (
                    <EditLink
                      goto={BookingJourneyStep.Payment}
                      ariaLabel={`${t("edit")} ${t("payment")}`}
                    />
                  )}
                </Grid>
              </li>
            )}
            {enableBusinessUnit() && (
              <li>
                <Grid size={{ xs: 12 }} className={classes.filledInfo}>
                  <BusinessOutlined className={classes.icon} />
                  <span>
                    {t("business_unit")}: {productDetails.businessUnit || t("not_provided")}
                  </span>
                  {showEditLink && (
                    <EditLink
                      goto={BookingJourneyStep.Payment}
                      ariaLabel={`${t("edit")} ${t("payment")}`}
                    />
                  )}
                </Grid>
              </li>
            )}
          </>
        )}
      </ul>
      <ConfirmationPopup
        open={showConfirmDialog}
        title={t("delete_popup_title")}
        message={t("delete_popup_message")}
        handleConfirmed={handleDeleteProduct}
        handleClose={() => setShowConfirmDialog(false)}
      />
    </Grid>
  );
};
